import { assertProjectAllowed, assertSkillAllowed, ProjectRegistryError, } from "../project-skill-registry/index.js";
import { DEFAULT_TASK_EXPIRY_MS, DEFAULT_TASK_TIMEOUT_MS, isMutableCwjTaskStatus, isTerminalCwjTaskStatus, isValidCwjTaskTransition, } from "./constants.js";
import { CwjTaskEngineError } from "./types.js";
import { MemoryCwjTaskStore } from "./memoryTaskStore.js";
export { MemoryCwjTaskStore };
export class CwjTaskEngine {
    constructor(options = {}) {
        this.emergencyStops = new Map();
        this.runningControllers = new Map();
        const now = () => Date.now();
        const gen = () => `task-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        this.options = {
            defaultTimeoutMs: options.defaultTimeoutMs ?? DEFAULT_TASK_TIMEOUT_MS,
            defaultExpiryMs: options.defaultExpiryMs ?? DEFAULT_TASK_EXPIRY_MS,
            generateTaskId: options.generateTaskId ?? gen,
            clock: options.clock ?? now,
            store: options.store ?? new MemoryCwjTaskStore(),
        };
        this.store = this.options.store;
    }
    now() {
        return this.options.clock();
    }
    generateEventId() {
        return `evt-${this.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    }
    generateTaskId(input) {
        return input.taskId ?? input.idempotencyKey ?? this.options.generateTaskId();
    }
    createAuditEvent(task, previousStatus, actorId, eventType, reason) {
        return {
            eventId: this.generateEventId(),
            eventType,
            taskId: task.taskId,
            status: task.status,
            previousStatus,
            actorId,
            reason,
            timestamp: this.now(),
        };
    }
    getRelevantScopes(context) {
        const scopes = ["global"];
        if (context.projectId)
            scopes.push(`project:${context.projectId}`);
        if (context.skillId)
            scopes.push(`skill:${context.skillId}`);
        if (context.tenantId)
            scopes.push(`tenant:${context.tenantId}`);
        return scopes;
    }
    scopeMatches(context, scope) {
        if (scope === "global")
            return true;
        const [type, id] = scope.split(":");
        if (!id)
            return false;
        switch (type) {
            case "project":
                return context.projectId === id;
            case "skill":
                return context.skillId === id;
            case "tenant":
                return context.tenantId === id;
            default:
                return false;
        }
    }
    assertNotEmergencyStopped(task) {
        const scopes = this.getRelevantScopes(task.context);
        for (const scope of scopes) {
            const record = this.emergencyStops.get(scope);
            if (record && record.active && task.emergencyStopEpoch < record.epoch) {
                throw new CwjTaskEngineError(`Emergency Stop active for ${scope}: ${record.reason ?? "no reason"}`, "emergency_stop");
            }
        }
    }
    async assertTaskScope(context, operation) {
        try {
            assertProjectAllowed(context.projectId, context.tenantId, context.ownerId, operation);
            assertSkillAllowed(context.skillId, context.projectId, context.tenantId, context.ownerId);
        }
        catch (err) {
            if (err instanceof ProjectRegistryError) {
                throw new CwjTaskEngineError(`invalid_scope: ${err.message}`, "invalid_scope");
            }
            throw err;
        }
    }
    assertActorAuthorized(context, actorId, action) {
        if (actorId !== context.ownerId) {
            throw new CwjTaskEngineError(`actor_unauthorized: actor ${actorId} is not authorized to ${action} task owned by ${context.ownerId ?? "<none>"}`, "actor_unauthorized");
        }
    }
    async applyExpiry(task) {
        if (!isMutableCwjTaskStatus(task.status))
            return task;
        const expiresAt = task.input.expiresAt;
        if (expiresAt !== undefined && this.now() >= expiresAt) {
            return this.transition(task, "expired", "system", "task_expired", "Lifetime exceeded");
        }
        return task;
    }
    async loadAndExpire(taskId) {
        const task = await this.store.load(taskId);
        if (!task)
            return undefined;
        return this.applyExpiry(task);
    }
    setEmergencyStop(scope, active, reason) {
        const current = this.emergencyStops.get(scope) ?? { active: false, epoch: 0 };
        let epoch = current.epoch;
        if (active) {
            epoch += 1;
            this.abortRunningTasksInScope(scope, reason);
        }
        this.emergencyStops.set(scope, { active, epoch, reason });
    }
    isEmergencyStopped(scope) {
        if (!scope)
            return false;
        return this.emergencyStops.get(scope)?.active ?? false;
    }
    abortRunningTasksInScope(scope, reason) {
        for (const [, entry] of this.runningControllers.entries()) {
            if (this.scopeMatches(entry.context, scope)) {
                entry.controller.abort(new CwjTaskEngineError(`Emergency Stop ${scope}: ${reason ?? ""}`, "emergency_stop"));
            }
        }
    }
    sameInput(a, b) {
        return (a.title === b.title &&
            a.description === b.description &&
            a.priority === b.priority &&
            a.requiresApproval === b.requiresApproval &&
            a.idempotencyKey === b.idempotencyKey &&
            JSON.stringify(a.payload) === JSON.stringify(b.payload));
    }
    async createTask(context, input, actorId) {
        await this.assertTaskScope(context, "write");
        this.assertActorAuthorized(context, actorId, "create");
        const stopEpoch = this.getCurrentEmergencyEpoch(context);
        if (stopEpoch > 0) {
            throw new CwjTaskEngineError("Emergency Stop active", "emergency_stop");
        }
        const taskId = this.generateTaskId(input);
        const existing = await this.store.load(taskId);
        if (existing) {
            if (existing.context.projectId === context.projectId &&
                existing.context.skillId === context.skillId &&
                existing.context.tenantId === context.tenantId &&
                existing.context.ownerId === context.ownerId &&
                this.sameInput(existing.input, input)) {
                return existing;
            }
            throw new CwjTaskEngineError(`idempotency_mismatch: Task ${taskId} already exists with different context/input`, "idempotency_mismatch");
        }
        const now = this.now();
        const expiresAt = input.expiresAt ?? now + this.options.defaultExpiryMs;
        const task = {
            taskId,
            context,
            input: { ...input, expiresAt },
            status: "pending",
            output: undefined,
            error: undefined,
            version: 1,
            createdAt: now,
            updatedAt: now,
            startedAt: undefined,
            completedAt: undefined,
            emergencyStopEpoch: 0,
            auditEvents: [],
        };
        task.auditEvents = [this.createAuditEvent(task, undefined, actorId, "task_created")];
        await this.store.save(task, 0);
        return task;
    }
    getCurrentEmergencyEpoch(context) {
        let epoch = 0;
        for (const scope of this.getRelevantScopes(context)) {
            const record = this.emergencyStops.get(scope);
            if (record && record.active && record.epoch > epoch) {
                epoch = record.epoch;
            }
        }
        return epoch;
    }
    async getTask(taskId) {
        return this.loadAndExpire(taskId);
    }
    async listTasks(filters) {
        const tasks = await this.store.list(filters);
        const expired = [];
        for (const task of tasks) {
            const updated = await this.applyExpiry(task);
            if (updated !== task)
                expired.push(updated);
        }
        if (expired.length > 0) {
            const expiredIds = new Set(expired.map((t) => t.taskId));
            return tasks.map((t) => (expiredIds.has(t.taskId) ? expired.find((e) => e.taskId === t.taskId) : t));
        }
        return tasks;
    }
    async approveTask(taskId, actorId) {
        let task = await this.requireTask(taskId);
        this.assertNotEmergencyStopped(task);
        await this.assertTaskScope(task.context, "write");
        this.assertActorAuthorized(task.context, actorId, "approve");
        if (task.status !== "approval_required") {
            throw new CwjTaskEngineError(`Task ${taskId} is not awaiting approval (status=${task.status})`, "invalid_transition");
        }
        task = await this.transition(task, "running", actorId, "task_approved");
        return task;
    }
    async executeTask(taskId, actorId, runner) {
        let task = await this.requireTask(taskId);
        this.assertNotEmergencyStopped(task);
        await this.assertTaskScope(task.context, "write");
        this.assertActorAuthorized(task.context, actorId, "execute");
        if (task.status === "completed")
            return task;
        if (task.status === "running") {
            if (this.runningControllers.has(taskId)) {
                throw new CwjTaskEngineError(`already_running: Task ${taskId} is already running`, "already_running");
            }
        }
        if (task.status === "approval_required") {
            throw new CwjTaskEngineError(`Task ${taskId} requires approval before execution`, "approval_required");
        }
        if (task.status === "pending" && task.input.requiresApproval) {
            task = await this.transition(task, "approval_required", actorId, "approval_required");
            return task;
        }
        if (!isMutableCwjTaskStatus(task.status)) {
            throw new CwjTaskEngineError(`Task ${taskId} is in terminal state ${task.status}`, "terminal_task");
        }
        if (task.status !== "running") {
            task = await this.transition(task, "running", actorId, "task_started");
        }
        const controller = new AbortController();
        this.runningControllers.set(taskId, { controller, context: task.context });
        const timeoutMs = task.input.maxDurationMs ?? this.options.defaultTimeoutMs;
        const timer = setTimeout(() => {
            controller.abort(new CwjTaskEngineError("Task execution timed out", "timeout"));
        }, timeoutMs);
        try {
            const result = runner ? await runner({ task, signal: controller.signal, timeoutMs }) : undefined;
            task = await this.requireTask(taskId);
            if (isTerminalCwjTaskStatus(task.status)) {
                return task;
            }
            const finalStatus = result?.status ?? "completed";
            if (!isValidCwjTaskTransition(task.status, finalStatus)) {
                return this.transition(task, "failed", actorId, "task_failed", `Runner returned invalid transition target ${finalStatus}`);
            }
            return this.transition(task, finalStatus, actorId, `task_${finalStatus}`, result?.error, result?.output);
        }
        catch (err) {
            task = await this.requireTask(taskId);
            if (isTerminalCwjTaskStatus(task.status)) {
                return task;
            }
            const message = err instanceof Error ? err.message : String(err);
            return this.transition(task, "failed", actorId, "task_failed", message);
        }
        finally {
            clearTimeout(timer);
            this.runningControllers.delete(taskId);
        }
    }
    async updateTask(taskId, updates, expectedVersion, actorId) {
        const task = await this.requireTask(taskId);
        this.assertNotEmergencyStopped(task);
        await this.assertTaskScope(task.context, "write");
        this.assertActorAuthorized(task.context, actorId, "update");
        if (task.version !== expectedVersion) {
            throw new CwjTaskEngineError(`concurrency: expected version ${expectedVersion}, found ${task.version}`, "concurrency");
        }
        if (!isMutableCwjTaskStatus(task.status)) {
            throw new CwjTaskEngineError(`Task ${taskId} cannot be updated in state ${task.status}`, "invalid_transition");
        }
        const nextInput = {
            ...task.input,
            ...updates,
        };
        const next = {
            ...task,
            input: nextInput,
            version: task.version + 1,
            updatedAt: this.now(),
            auditEvents: [
                ...task.auditEvents,
                this.createAuditEvent(task, task.status, actorId, "task_updated", "input updated"),
            ],
        };
        await this.store.save(next, expectedVersion);
        return next;
    }
    async cancelTask(taskId, actorId) {
        let task = await this.requireTask(taskId);
        await this.assertTaskScope(task.context, "write");
        this.assertActorAuthorized(task.context, actorId, "cancel");
        const entry = this.runningControllers.get(taskId);
        if (entry) {
            entry.controller.abort(new CwjTaskEngineError("Task cancelled by user", "cancelled"));
        }
        if (!isMutableCwjTaskStatus(task.status)) {
            throw new CwjTaskEngineError(`Task ${taskId} cannot be cancelled in state ${task.status}`, "invalid_transition");
        }
        task = await this.transition(task, "cancelled", actorId, "task_cancelled");
        return task;
    }
    async pauseTask(taskId, actorId) {
        const task = await this.requireTask(taskId);
        this.assertNotEmergencyStopped(task);
        await this.assertTaskScope(task.context, "write");
        this.assertActorAuthorized(task.context, actorId, "pause");
        if (this.runningControllers.has(taskId)) {
            throw new CwjTaskEngineError(`pause_denied: Task ${taskId} has an active runner and cannot be paused without a cooperative checkpoint`, "pause_denied");
        }
        if (task.status !== "pending" && task.status !== "approval_required") {
            throw new CwjTaskEngineError(`Task ${taskId} cannot be paused in state ${task.status}`, "invalid_transition");
        }
        return this.transition(task, "paused", actorId, "task_paused");
    }
    async resumeTask(taskId, actorId) {
        const task = await this.requireTask(taskId);
        this.assertNotEmergencyStopped(task);
        await this.assertTaskScope(task.context, "write");
        this.assertActorAuthorized(task.context, actorId, "resume");
        if (task.status !== "paused") {
            throw new CwjTaskEngineError(`Task ${taskId} cannot be resumed in state ${task.status}`, "invalid_transition");
        }
        const pauseEvent = [...task.auditEvents]
            .reverse()
            .find((e) => e.eventType === "task_paused");
        const previousStatus = pauseEvent?.previousStatus ?? "pending";
        if (!isValidCwjTaskTransition(task.status, previousStatus)) {
            throw new CwjTaskEngineError(`Task ${taskId} cannot be resumed from paused to ${previousStatus}`, "invalid_transition");
        }
        return this.transition(task, previousStatus, actorId, "task_resumed");
    }
    async requireTask(taskId) {
        const task = await this.loadAndExpire(taskId);
        if (!task) {
            throw new CwjTaskEngineError(`Task not found: ${taskId}`, "not_found");
        }
        return task;
    }
    async transition(task, to, actorId, eventType, errorOrReason, output) {
        if (!isValidCwjTaskTransition(task.status, to)) {
            throw new CwjTaskEngineError(`Invalid transition from ${task.status} to ${to}`, "invalid_transition");
        }
        const previous = task.status;
        const now = this.now();
        const next = {
            ...task,
            status: to,
            version: task.version + 1,
            updatedAt: now,
        };
        if (to === "running" && !task.startedAt) {
            next.startedAt = now;
        }
        if (isTerminalCwjTaskStatus(to)) {
            next.completedAt = now;
        }
        if (output !== undefined) {
            next.output = output;
        }
        if (errorOrReason !== undefined && to === "failed") {
            next.error = errorOrReason;
        }
        next.auditEvents = [
            ...task.auditEvents,
            this.createAuditEvent(next, previous, actorId, eventType, errorOrReason),
        ];
        await this.store.save(next, task.version);
        return next;
    }
}
//# sourceMappingURL=taskEngine.js.map